import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Calendar } from "lucide-react";
import { useCoachContext } from "@/contexts/CoachContext";
import { supabase } from "@/integrations/supabase/client";
import defaultLogo from "@/assets/gocoachify-logo.webp";
import LanguageSelector from "@/components/LanguageSelector";
import BookDemoDialog from "@/components/BookDemoDialog";

interface SectionVisibility {
  show_about: boolean;
  show_services: boolean;
  show_packages: boolean;
  show_contact: boolean;
}

const Header = () => {
  const { coachId } = useCoachContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string>("GoCoachify");
  const [visibility, setVisibility] = useState<SectionVisibility>({
    show_about: true,
    show_services: true,
    show_packages: true,
    show_contact: true,
  });

  useEffect(() => {
    if (coachId) {
      const fetchCoachData = async () => {
        const { data } = await supabase
          .from("coaches")
          .select("logo_url, business_name")
          .eq("id", coachId)
          .single();

        if (data) {
          setCustomLogo(data.logo_url);
          setBusinessName(data.business_name || "GoCoachify");
        }

        const { data: contentData } = await supabase
          .from("coach_website_content")
          .select("show_about, show_services, show_packages, show_contact")
          .eq("coach_id", coachId)
          .maybeSingle();

        if (contentData) {
          setVisibility({
            show_about: contentData.show_about ?? true,
            show_services: contentData.show_services ?? true,
            show_packages: contentData.show_packages ?? true,
            show_contact: contentData.show_contact ?? true,
          });
        }
      };
      fetchCoachData();
    }
  }, [coachId]);

  const allNavLinks = coachId ? [
    { href: "#about", label: "About", visible: visibility.show_about },
    { href: "#services", label: "Services", visible: visibility.show_services },
    { href: "#packages", label: "Packages", visible: visibility.show_packages },
    { href: "/blog", label: "Blog", isRoute: true, visible: true },
    { href: "/podcast", label: "Podcast", isRoute: true, visible: true },
    { href: "/courses", label: "Courses", isRoute: true, visible: true },
    { href: "#contact", label: "Contact", visible: visibility.show_contact },
  ] : [
    { href: "/about", label: "About Us", isRoute: true, visible: true },
    { href: "/coaches", label: "Coaches", isRoute: true, visible: true },
    { href: "/blog", label: "Blog", isRoute: true, visible: true },
    { href: "/podcast", label: "Podcast", isRoute: true, visible: true },
    { href: "/courses", label: "Courses", isRoute: true, visible: true },
    { href: "/toolkit", label: "Toolkit", isRoute: true, visible: true },
    { href: "/pricing", label: "Pricing", isRoute: true, visible: true },
    { href: "/success-stories", label: "Success Stories", isRoute: true, visible: true },
    { href: "/contact", label: "Contact", isRoute: true, visible: true },
  ];

  const navLinks = allNavLinks.filter(link => link.visible);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-xl border-b border-border shadow-sm">
      <div className="container mx-auto px-4 md:px-6">
        <nav className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to={coachId ? "#" : "/"} className="flex items-center flex-shrink-0">
            {customLogo ? (
              <img 
                src={customLogo} 
                alt={businessName} 
                className="h-10 w-auto max-w-[160px] object-contain"
              />
            ) : coachId ? (
              <span className="text-base font-semibold text-foreground tracking-tight">
                {businessName}
              </span>
            ) : (
              <img 
                src={defaultLogo} 
                alt="GoCoachify - AI Coaching Platform logo" 
                className="h-10 w-auto"
              />
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm font-medium whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm font-medium whitespace-nowrap"
                >
                  {link.label}
                </a>
              )
            ))}
          </div>

          {/* Right side: Language + CTAs */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <LanguageSelector />
            <BookDemoDialog>
              <Button variant="outline" size="sm" className="gap-2">
                <Calendar className="w-4 h-4" />
                Book Demo
              </Button>
            </BookDemoDialog>
            <Button size="sm" asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/coach/auth?mode=signup">Get Started</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-1.5 text-foreground"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border animate-fade-in pb-4">
            <div className="flex flex-col py-3 gap-1">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm py-2 px-2 rounded-md hover:bg-muted"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm py-2 px-2 rounded-md hover:bg-muted"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                )
              ))}
              <div className="pt-3 flex flex-col gap-2 border-t border-border mt-2">
                <LanguageSelector />
                <BookDemoDialog>
                  <Button variant="outline" size="sm" className="w-full gap-2">
                    <Calendar className="w-4 h-4" />
                    Book Demo
                  </Button>
                </BookDemoDialog>
                <Button size="sm" asChild className="w-full">
                  <Link to="/coach/auth?mode=signup">Get Started</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
